import React, { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import ReactModal from "react-modal";
import classNames from "classnames"
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useIntl } from "react-intl";
import {
  showSuccessNotification,
  showErrorNotification,
  showTreatmentNotification,
  SCAN_NOTIFICATION_DURATION_MS,
} from "../../../../utils/Notification";
import images from "../../../../images";
import * as workerActions from "../../../../redux/Worker/actions";
import InputText from "../../../Input/Text";
import Autocomplete from "../../../Autocomplete"
import messages from "./messages";
import Button from "../../../Button";
import { Site } from "../../../../models/Interfaces/Site";
import { Worker } from "../../../../models/Interfaces/Worker";
import { Company } from "../../../../models/Interfaces/Company"
import ModalCamera from "../../Camera";
import ModalHeader from "../../ModalHeader"
import ModalClose from "../../ModalClose"
import { ProcessingOverlay, CardStatusDialog, ScanFieldHint } from "../../ScanCard";
import { processBtpImage } from "../../../../utils/scan/processBtpImage";
import { showScanFeedbackNotification } from "../../../../utils/scan/scanFeedback";
import type { ScanResult } from "../../../../types/scan";
import { fuzzyMatchCompany } from "../../../../utils/scan/fuzzyMatchCompany";
import {
  buildBtpScanBaseline,
  buildSubmittedScanValues,
  trackBtpCardScan,
  BtpScanBaseline,
} from "../../../../utils/scan/trackBtpCardScan";
import { useThunkDispatch } from "../../../../redux/hooks";
import { useSelector } from "react-redux";
import { AppState } from "../../../../redux/reducers";
import { trackPromise } from "react-promise-tracker";
import OfflineDetector from "../../../../services/OfflineDetector";

interface Props {
  isOpen: boolean;
  onClick: () => void;
  site: Site;
  worker: Worker;
  forceOpenCamera: boolean
  setForceOpenCamera: React.Dispatch<React.SetStateAction<boolean>>
}

const customStyles = {
  content: {
    top: 0,
    padding: 0,
    background: "#ffffff"
  },
  overlay: {
    zIndex: 9999
  }
};

const usePrevious = value => {
  const ref = useRef<Worker>()

  useEffect(() => {
    ref.current = value
  })

  return ref.current
}

const ModalUpdateWorker = (props: Props) => {
  const [dataUri, setDataUri] = useState("");
  const [showModalCamera, setShowModalCamera] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [partialScanResult, setPartialScanResult] = useState<Partial<ScanResult> | null>(null);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [scanBaseline, setScanBaseline] = useState<BtpScanBaseline | null>(null);
  const [companiesAvailable, setCompaniesAvailable] = useState([] as Company[]);
  const [hasFocusedField, setHasFocusedField] = useState<undefined | boolean>();
  const [cardStatusDialog, setCardStatusDialog] = useState<{ open: boolean; status?: "valid" | "invalid"; message?: string }>({ open: false });
  const refForm = useRef<FormikProps<any>>(null);

  const dispatch = useThunkDispatch();
  const intl = useIntl();
  const tokenFromRedux = useSelector((state: AppState) => state.authentication.token);
  const backendBaseUrl = process.env.REACT_APP_API_BACKEND_URL;
  const token =
    tokenFromRedux ??
    (() => {
      try {
        const raw = localStorage.getItem("reduxPersist:authentication");
        if (!raw) return null;
        const data = JSON.parse(raw) as { token?: string | null };
        return data?.token ?? null;
      } catch {
        return null;
      }
    })();

  const { worker, forceOpenCamera, setForceOpenCamera } = props

  const previousWorker = usePrevious(worker)

  useEffect(() => {
    if (!showModalCamera && forceOpenCamera) {
      setShowModalCamera(true)
    }
  }, [worker, showModalCamera, forceOpenCamera])

  useEffect(() => {
    if (previousWorker && previousWorker?.id !== worker.id) {
      setDataUri("");
      setScanResult(null);
      setScanBaseline(null);
    }
  }, [worker, previousWorker]);

  useEffect(() => {
    if (props.site) {
      const companies = props.site.companies ? props.site.companies : []
      setCompaniesAvailable(companies);
    }
  }, [props.site]);

  useEffect(() => {
    if (!scanResult) {
      return;
    }
    const companyFromScan = scanResult.company?.trim();
    const matchedCompany =
      companyFromScan && companiesAvailable.length > 0
        ? fuzzyMatchCompany(companyFromScan, companiesAvailable)
        : null;
    setScanBaseline(
      buildBtpScanBaseline(
        scanResult,
        matchedCompany ?? companyFromScan ?? ""
      )
    );
  }, [scanResult, companiesAvailable]);

  const validationSchema = Yup.object({
    firstname: Yup.string().required(
      intl.formatMessage(messages.firstNameRequired)
    ),
    lastname: Yup.string().required(
      intl.formatMessage(messages.lastNameRequired)
    ),
    company: Yup.mixed()
      .test(
        "company-required",
        intl.formatMessage(messages.companyNameRequired),
        (v) =>
          (typeof v === "string" && v.trim() !== "") ||
          (v && typeof v === "object" && typeof (v as { name?: string }).name === "string" && (v as { name: string }).name.trim() !== "")
      )
      .required(intl.formatMessage(messages.companyNameRequired)),
    btp: Yup.string() // .required(intl.formatMessage(messages.btpNumberRequired))
  });

  const handleTakePhotoAnimationDone = async (newDataUri: string, qrUrlFromLive?: string, extraFrames?: string[]) => {
    flushSync(() => {
      setIsProcessing(true);
      setPartialScanResult(null);
      setScanResult(null);
      setScanBaseline(null);
    });
    try {
      const result = await processBtpImage(
        newDataUri,
        (_step, partial) => {
          if (partial) setPartialScanResult((prev) => ({ ...prev, ...partial }));
        },
        {
          ...(qrUrlFromLive ? { qrUrl: qrUrlFromLive } : {}),
          ...(extraFrames?.length ? { extraFramesForOcr: extraFrames } : {}),
          ...(backendBaseUrl && token
            ? { backendQrConfig: { baseUrl: backendBaseUrl, token } }
            : {}),
        }
      );
      setDataUri(result.btp_card_image);
      setScanResult(result);
      if (result.ocrTimedOut) {
        showTreatmentNotification(
          "Lecture automatique impossible, veuillez saisir les informations.",
          SCAN_NOTIFICATION_DURATION_MS
        );
      }
      showScanFeedbackNotification(result);
      setTimeout(() => {
        setShowModalCamera(false);
        setForceOpenCamera(false);
        setIsProcessing(false);
        setPartialScanResult(null);
        const qr = result.qrResult;
        if (qr?.status === "valid" || qr?.status === "invalid") {
          setCardStatusDialog({ open: true, status: qr.status, message: qr.status === "invalid" ? (qr.message ?? undefined) : undefined });
        }
      }, 2000);
    } catch {
      setDataUri(newDataUri);
      setShowModalCamera(false);
      setForceOpenCamera(false);
      setIsProcessing(false);
    }
  };

  const handleUpdateWorker = (
    values: {
      firstname: string;
      lastname: string;
      company: string | Company;
      btp: string;
    },
    setSubmitting: any
  ) => {
    const companyName =
      typeof values.company === "string"
        ? values.company
        : values.company?.name ?? "";

    const payload = {
      id: props.worker.id,
      firstname: values.firstname,
      lastname: values.lastname,
      company: {
        name: companyName
      },
      btp_number: values.btp,
      btp_card_image: dataUri,
      site: props.site
    };


    if (OfflineDetector.appIsOnline()) {
      trackPromise(
        dispatch(workerActions.updateWorkerDispatch(payload, props.worker))
          .then(res => {
            if (scanBaseline) {
              trackBtpCardScan({
                token,
                workerId: props.worker.id,
                siteId: props.site?.id,
                baseline: scanBaseline,
                submitted: buildSubmittedScanValues(values),
                context: "update",
              });
            }
            showSuccessNotification(
              "Le travailleur a été mis à jour avec succès"
            );
          })
          .catch(err => {
            showErrorNotification("Le travailleur n'a pas été mis à jour");
          })
      );
      props.onClick();
    } else {
      dispatch(workerActions.updateWorkerDispatch(payload, props.worker));
      props.onClick();
    }

    // reset photo
    setShowModalCamera(false)
    setDataUri("")
  };

  const companyFromScan = scanResult?.company?.trim();
  const matchedCompany =
    companyFromScan && companiesAvailable.length > 0
      ? fuzzyMatchCompany(companyFromScan, companiesAvailable)
      : null;
  const initialValues = {
    firstname: scanResult?.firstname ?? props.worker.firstname,
    lastname: scanResult?.lastname ?? props.worker.lastname,
    company:
      matchedCompany ??
      companyFromScan ??
      (props.worker.company ? props.worker.company.name : ""),
    btp: scanResult?.btp_number ?? props.worker.btp_number ?? "",
    btpCardNumber: props.worker.btp_card_image
  };

  const worker_btp_card_image = dataUri ? dataUri : worker.btp_card_image

  const handleHasFocusedField = () => {
    setHasFocusedField(true)
  }

  const handleHasnotFocusedField = () => {
    setHasFocusedField(false)
  }

  const closeModal = <ModalClose onClick={props.onClick} />

  const addonRightClassName = classNames("absolute right-20px self-center text-socotec-blue-100 text-15px", {
    "opacity-0": hasFocusedField === undefined,
    "hidden-with-delay": hasFocusedField === false
  })

  const addonRight = <div className={addonRightClassName} onClick={refForm.current?.submitForm}>{intl.formatMessage(messages.save)}</div>

  return (
    <>
    <ReactModal
      isOpen={props.isOpen}
      contentLabel="modal-worker-new"
      appElement={document.getElementById("root")}
      style={customStyles}
    >
      <ModalHeader sticky title={intl.formatMessage(messages.title)} closeButton={closeModal} addonRight={addonRight} />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          handleUpdateWorker(values, setSubmitting);
        }}
        innerRef={refForm}
      >
        {({
          handleSubmit,
          handleChange,
          isSubmitting,
          values,
          errors,
          touched,
          setFieldValue
        }) => (
          <form
            className="flex flex-col mt-5 px-10 flex-grow justify-between bg-white"
            onSubmit={handleSubmit}
            style={{ paddingBottom: "60px" }}
          >
            <div className="flex flex-col my-3">
              <div className="flex flex-col mb-1">
                <div
                  className="relative flex flex-col items-center justify-center cursor-pointer w-full border-1px border-dashed border-socotec-gray-100 py-10 px-4 bg-socotec-white-100 rounded-20px"
                  style={{ minHeight: "175px" }}
                  onClick={() => {
                    setForceOpenCamera(false)
                    setShowModalCamera(true);
                  }}
                >
                  {worker_btp_card_image && (
                    <div className="absolute top-0 left-0 right-0 bottom-0 w-full h-full bg-center bg-no-repeat bg-cover rounded-20px" style={{ backgroundImage: `url(${worker_btp_card_image})` }}>
                      <div className="absolute" style={{ top: "-10px", right: "-10px" }}><img src={images.retry} alt="Recommencer" /></div>
                    </div>
                  )}
                  {!worker_btp_card_image && (
                    <>
                      <img src={images.photo} alt="#" className="mb-4" />
                      <span className="text-socotec-gray-100 text-18px">{intl.formatMessage(messages.takePicture)}</span>
                    </>
                  )}
                </div>
                <ModalCamera
                  isOpen={showModalCamera}
                  onClick={() => {
                    if (!isProcessing) {
                      setForceOpenCamera(false);
                      setShowModalCamera(false);
                    }
                  }}
                  btpCardNumber={values.btpCardNumber}
                  btpNumber={values.btp}
                  dataUri={dataUri}
                  setDataUri={setDataUri}
                  onTakePhoto={handleTakePhotoAnimationDone}
                  overlayContent={isProcessing ? <ProcessingOverlay partialResult={partialScanResult} result={scanResult} /> : undefined}
                  liveQrMode
                />
              </div>

              <div className="mb-6" />

              <InputText
                id="firstname"
                name="firstname"
                type="text"
                value={values.firstname}
                placeholder={intl.formatMessage(messages.firstname)}
                label={intl.formatMessage(messages.firstname)}
                onChange={handleChange}
                onFocus={handleHasFocusedField}
                onBlur={handleHasnotFocusedField}
                className="mb-1"
              />
              {errors.firstname && touched.firstname && (
                <div className="text-red-700 font-normal font-roboto italic text-sm">
                  {String(errors.firstname)}
                </div>
              )}
              <ScanFieldHint
                field="firstname"
                value={values.firstname}
                fieldConfidence={scanResult?.fieldConfidence}
              />

              <div className="flex flex-row justify-end mt-1">
                <img src={images.echanger} alt="Permuter" onClick={() => {
                  const currentFirstname = values.firstname
                  const currentLastname = values.lastname

                  setFieldValue("firstname", currentLastname)
                  setFieldValue("lastname", currentFirstname)
                }}></img>
              </div>

              <div className="mb-2" />

              <InputText
                id="lastname"
                name="lastname"
                type="text"
                value={values.lastname}
                placeholder={intl.formatMessage(messages.lastname)}
                label={intl.formatMessage(messages.lastname)}
                onChange={handleChange}
                onFocus={handleHasFocusedField}
                onBlur={handleHasnotFocusedField}
                className="mb-1"
              />
              {errors.lastname && touched.lastname && (
                <div className="text-red-700 font-normal font-roboto italic text-sm">
                  {String(errors.lastname)}
                </div>
              )}
              <ScanFieldHint
                field="lastname"
                value={values.lastname}
                fieldConfidence={scanResult?.fieldConfidence}
              />

              <div className="mb-6" />

              <Autocomplete
                id="company"
                name="company"
                placeholder={intl.formatMessage(messages.company)}
                suggestions={companiesAvailable}
                value={values.company}
                onChange={value => setFieldValue('company', value)}
                onFocus={handleHasFocusedField}
                onBlur={handleHasnotFocusedField}
                onFilter={(suggestion) => setFieldValue("company", suggestion)}
                keySearch="name"
                className="mb-1 z-2"
              />
              {errors.company && touched.company && (
                <div className="text-red-700 font-normal font-roboto italic text-sm">
                  {String(errors.company)}
                </div>
              )}
              <ScanFieldHint
                field="company"
                value={typeof values.company === "string" ? values.company : values.company?.name}
                fieldConfidence={scanResult?.fieldConfidence}
              />

              <div className="mb-6" />

              <InputText
                id="btp"
                name="btp"
                type="text"
                value={values.btp}
                placeholder={intl.formatMessage(messages.btpNumber)}
                label={intl.formatMessage(messages.btpNumber)}
                onChange={handleChange}
                onFocus={handleHasFocusedField}
                onBlur={handleHasnotFocusedField}
                className="mb-1 z-1"
              />
              {errors.btp && touched.btp && (
                <div className="text-red-700 font-normal font-roboto italic text-sm mt-1">
                  {String(errors.btp)}
                </div>
              )}
            </div>

            {hasFocusedField !== true && (
              <div className="fixed bottom-20px left-10 right-10 z-3000" style={{ width: "calc(100% - 5rem)" }}>
                <Button
                  disabled={
                    isSubmitting ||
                    !values.firstname ||
                    !values.lastname ||
                    !values.company
                  }
                >
                  <span className="font-roboto">
                    {intl.formatMessage(messages.save)}
                  </span>
                </Button>
              </div>
            )}
          </form>
        )}
      </Formik>
    </ReactModal>
    {cardStatusDialog.open && cardStatusDialog.status && (
      <CardStatusDialog
        isOpen
        onClose={() => setCardStatusDialog({ open: false })}
        status={cardStatusDialog.status}
        message={cardStatusDialog.message}
      />
    )}
    </>
  );
};

export default ModalUpdateWorker;
